import { i18n } from '@i18n/index'
import { useLanguage } from '@i18n/useLanguage'
import { useAuthState, useClient } from '@based/sdk'
import { useCallback, useEffect, useState } from 'react'
import { fullName } from '@app/util/helpers'
import { TagEditorPanel } from '../shared/TagEditorPanel'
import { ProfileEdit } from 'functions/app/dashboard/profile/edit'
import { TagModel } from '@app/types/TagModel'
import { PersonalProfileModel } from 'functions/app/dashboard/profile/view'

export const ProfileTagsEditPanels = ({
  profile,
}: {
  profile: PersonalProfileModel
}) => {
  const client = useClient()
  const [language, _setLanguage] = useLanguage()
  const translations = i18n.languages[language].app.dashboard.profile
  const authState = useAuthState()
  const [allRegions, setAllRegions] = useState<TagModel[]>([])
  const [allHousingForms, setAllHousingForms] = useState<TagModel[]>([])
  const [allInterests, setInterests] = useState<TagModel[]>([])

  useEffect(() => {
    const closeR = client.query('suggestions-region').subscribe((r) => {
      const result: TagModel[] = r.map((x: { id: number; name: any }) => ({
        key: x.id,
        value: x.name,
      }))
      setAllRegions(result)
    })
    const closeHF = client.query('list-housing-forms').subscribe((r) => {
      const result: TagModel[] = r.map((x: { id: number; title: any }) => ({
        key: x.id,
        value: x.title,
      }))
      setAllHousingForms(result)
    })
    const closeI = client.query('list-user-interests').subscribe((r) => {
      const result: TagModel[] = r.map((x: { id: number; name: any }) => ({
        key: x.id,
        value: x.name,
      }))
      setInterests(result)
    })
    return () => {
      closeR()
      closeHF()
      closeI()
    }
  }, [client])

  const save = useCallback(
    async (value: ProfileEdit) => {
      return await client.call('edit-profile', { id: authState.userId, value })
    },
    [authState, client],
  )

  const saveSearchRegions = async (regions: TagModel[]) => {
    return await save({ searchRegions: regions.map((x) => ({ id: x.key })) })
  }
  const saveHousingForms = async (housingForms: TagModel[]) => {
    return await save({
      housingForms: housingForms.map((x) => ({ id: x.key })),
    })
  }
  const saveInterests = async (interests: TagModel[]) => {
    return await save({
      interests: interests.map((x) => ({ id: x.key })),
    })
  }

  const _name = fullName(profile)

  return (
    <>
      <TagEditorPanel
        title={translations.searchRegion}
        currentSelectionSubtitile={translations.yourRegions}
        selectNewSubtitile={translations.selectRegion}
        currentTags={profile.searchRegions.map((x) => ({
          key: x.id,
          value: x.name,
        }))}
        allTags={allRegions}
        onSave={saveSearchRegions}
      />
      <TagEditorPanel
        title={translations.housingForm}
        currentSelectionSubtitile={translations.yourHousingForms}
        selectNewSubtitile={translations.selectHousingForm}
        currentTags={profile.housingForms.map((x) => ({
          key: x.id,
          value: x.title,
        }))}
        allTags={allHousingForms}
        onSave={saveHousingForms}
      />
      <TagEditorPanel
        title={translations.interests}
        currentSelectionSubtitile={translations.yourInterests}
        selectNewSubtitile={translations.selectInterest}
        currentTags={profile.interests.map((x) => ({
          key: x.id,
          value: x.name,
        }))}
        allTags={allInterests}
        onSave={saveInterests}
      />
    </>
  )
}
